import { TEST_IDS } from '@va/constants';
import { useMergeRefs } from '@va/util/hooks';
import { useAriaListboxPattern } from '@va/util/misc';
import classNames from 'classnames';
import { forwardRef } from 'react';
import { fontWeights, Paragraph } from '../typography';
import { MenuListItem, MenuListItemType } from './MenuListItem';

type MenuListProps = {
  className?: string;
  items: MenuListItemType[];
  noItemsMessage?: string;
};

export const MenuList = forwardRef<HTMLUListElement, MenuListProps>(
  ({ className, items, noItemsMessage }: MenuListProps, ref) => {
    const {
      listboxProps: { ref: listboxRef, ...listboxProps },
    } = useAriaListboxPattern({ id: 'menu-list' });

    return (
      <ul
        ref={useMergeRefs([ref, listboxRef])}
        className={classNames('overflow-auto scrollbar scrollbar-thumb rounded-9px', className)}
        {...listboxProps}
      >
        {items.map((item, index, arr) => {
          const prevItem = arr[index - 1];
          const nextItem = arr[index + 1];
          const isPrevSelected = prevItem?.selected;
          const isNextSelected = nextItem?.selected;

          return (
            <MenuListItem
              id={item.id}
              data-testid={item['data-testid'] ?? TEST_IDS.helpers.createListItemId(item.id)}
              key={item.id}
              onClick={() => {
                item.onClick?.();
              }}
              className={classNames(
                {
                  'rounded-t-none': isPrevSelected && item.selected,
                  'rounded-b-none': isNextSelected && item.selected,
                },
                item.className,
              )}
              labelClassName={item.labelClassName}
              label={item.label}
              icon={item.icon}
              endAdornment={item.endAdornment}
              disabled={item.disabled}
              selected={item.selected}
              tooltip={item.tooltip}
              tooltipProps={item.tooltipProps}
              logicalOperator={item.logicalOperator}
              ref={item?.ref}
            />
          );
        })}
        {items.length === 0 && noItemsMessage && (
          <Paragraph className='py-3' weight={fontWeights.medium}>
            {noItemsMessage}
          </Paragraph>
        )}
      </ul>
    );
  },
);

MenuList.displayName = 'MenuList';
